import {
  AppBase,
  AppOptions,
  FILLMODE_FILL_WINDOW,
  RESOLUTION_AUTO,
  createGraphicsDevice,
  type ComponentSystem,
  type GraphicsDevice,
  type ResourceHandler,
} from 'playcanvas';
import type { PlatformAdapter } from '../platform/index';
import { resolveDeviceTypes, detectTier, type NavigatorHint, type TierResult } from './TierDetector';
import { assertSystemsRegistered } from './assertSystemsRegistered';
import { SceneRegistry } from './SceneRegistry';
import { warmup } from './WarmupRunner';
import { setupContextLoss } from './ContextLossGuard';

// @engine/core — createApp / defineGame / Module (REBUILD_PLAN Step 7a / D-D).
// The FROZEN boot lifecycle (guarantee 8) extracted from main.ts:26-116. The engine supplies the
// orchestration; the game supplies the config (modules, scenes, platform, tier params, hooks).
//
// TESTABILITY (the plan's R11 pattern): every PlayCanvas call flows through the `deps` parameter, which
// defaults to the real playcanvas functions. Tests inject hand-rolled stubs so the lifecycle-ordering
// test asserts the REAL createApp sequence, never a parallel reimplementation.

/** A pluggable module — contributes component systems and/or resource handlers to AppOptions. */
export interface Module {
  /**
   * Optional component system constructors to append to AppOptions.componentSystems. Entries are
   * CONCATENATED across all modules (hand-authored; derivation from classes is rejected — D-D, the
   * PlayCanvas 2.19.5 `id` is set in the CONSTRUCTOR, not statically).
   */
  componentSystems?: (typeof ComponentSystem)[];
  /**
   * Optional resource handler constructors to append to AppOptions.resourceHandlers. Defaults to an
   * empty array; the lifecycle always initialises it so a missing entry is not a footgun.
   */
  resourceHandlers?: (typeof ResourceHandler)[];
  /**
   * Optional post-init hook (called AFTER assertSystemsRegistered, BEFORE scene build). A no-op for
   * an empty list; position is preserved for future modules.
   */
  init?: (app: AppBase) => void | Promise<void>;
}

/** The fully-validated game configuration (returned by defineGame, consumed by createApp). */
export interface GameConfig {
  /**
   * Canvas element id to look up. Defaults to `'app'`. Throws loudly if not found (missing canvas is
   * always a programming error, not a recoverable condition).
   */
  canvas?: string;
  /** Modules that contribute component systems / resource handlers to the AppBase init. */
  modules: Module[];
  /** The SceneRegistry that maps `?scene=` values to scene builders. Must contain `defaultScene`. */
  scenes: SceneRegistry;
  /** The `?scene=` default (must be registered in `scenes`; defineGame validates this). */
  defaultScene: string;
  /**
   * The hand-authored list of required component system names to assert after app.init().
   * Derivation from classes is explicitly rejected (D-D / arch §1.1).
   */
  requiredSystems: readonly string[];
  /** The platform adapter (Poki / web / noop — picked by the game, never by the engine). */
  platform: PlatformAdapter;
  /** Tier / device-type config. */
  tier: {
    /** Whether every material in this game is WebGPU-eligible (gate-14 / D-D). */
    contentWebgpuEligible: boolean;
    /** Raw `?gfx` param value (from `new URLSearchParams(location.search).get('gfx')`). Null = no override. */
    force?: string | null;
    /**
     * Device-creation antialias (MSAA) request. Omitted → the engine default (`createGraphicsDevice`
     * defaults antialias to TRUE, d.ts:59084-59120) and the createGraphicsDevice call shape is
     * byte-identical to the pre-field behaviour. MSAA is device-creation-time shader-structural
     * state — it can never be a runtime quality knob, only a boot pin.
     */
    antialias?: boolean;
    /**
     * Game-side post-classification hook, applied to the detectTier result BEFORE
     * `device.maxPixelRatio` is written and before the first resizeCanvas — a corrected tier here
     * means the mis-sized backbuffer never exists. Receives the same unmasked renderer string and
     * navigator hints the classifier saw. Return the (possibly adjusted) TierResult; identity when
     * no adjustment applies. Omitted → detectTier verbatim (the demo path).
     */
    postClassify?: (
      tier: TierResult,
      ctx: { unmaskedRenderer: string; nav: NavigatorHint },
    ) => TierResult;
  };
  /** Optional lifecycle hooks — all default to no-op / undefined. */
  hooks?: {
    /**
     * Progress callback. Fires at named lifecycle milestones during createApp. Default: undefined
     * (the loading screen is deferred; this is the CONTRACT landing, not the screen). Receives a
     * string stage name from the frozen set below.
     */
    onProgress?: (stage: CreateAppStage) => void;
    /**
     * Ready callback. Fires after the full lifecycle completes (app.start(), warmup, context-loss
     * wiring, gameLoadingFinished).  uses this to publish `__app` and emit the boot log.
     */
    onReady?: (ctx: ReadyContext) => void;
  };
}

/** Named stages passed to `hooks.onProgress`. Ordered as the guarantee-8 lifecycle fires them. */
export type CreateAppStage =
  | 'device-created'
  | 'tier-detected'
  | 'app-init'
  | 'systems-asserted'
  | 'modules-init'
  | 'scene-built'
  | 'platform-init'
  | 'started'
  | 'warmed-up'
  | 'context-loss-wired'
  | 'ready';

/** The context passed to `hooks.onReady`. */
export interface ReadyContext {
  app: AppBase;
  device: GraphicsDevice;
  tier: TierResult;
  platform: PlatformAdapter;
}

/**
 * Validate a game config before passing it to createApp. Throws on:
 * - empty modules array
 * - empty scenes (no registered default — caught by SceneRegistry.resolve at runtime, but failing eagerly
 *   on defineGame is clearer)
 * - missing platform
 *
 * NOTE: the `scenes` argument is a SceneRegistry instance; `defaultScene` must be registered in it. The
 * validation calls `scenes.resolve('')` (no `?scene=` → default) to catch a missing registration EAGERLY
 * at defineGame time rather than at first page load.
 *
 * Returns the config unchanged (typed narrowing only — no mutation).
 */
export function defineGame(config: GameConfig): GameConfig {
  if (config.modules.length === 0) {
    throw new Error('defineGame: modules must be non-empty (at least one Module required).');
  }
  if (config.scenes == null) {
    throw new Error('defineGame: scenes must be a SceneRegistry instance.');
  }
  if (!config.platform) {
    throw new Error('defineGame: platform must be a PlatformAdapter instance.');
  }
  // Probe that the default scene is registered — SceneRegistry.resolve throws with a clear message if not.
  config.scenes.resolve(''); // no ?scene= → resolves the default
  return config;
}

// ── Internal deps seam (R11 testability pattern) ─────────────────────────────────────────────────
// Every PlayCanvas global call flows through this interface. Production uses the real functions.
// Tests inject hand-rolled stubs to assert the REAL createApp ordering without a browser DOM.

/** Internal dependency injection interface (testability without forking the production path). */
export interface _CreateAppDeps {
  getElementById(id: string): HTMLCanvasElement | null;
  createGraphicsDevice(
    canvas: HTMLCanvasElement,
    opts: { deviceTypes: string[]; antialias?: boolean },
  ): Promise<GraphicsDevice>;
  newAppBase(canvas: HTMLCanvasElement): AppBase;
  fillMode: typeof FILLMODE_FILL_WINDOW;
  resolutionMode: typeof RESOLUTION_AUTO;
}

/** The real production dependencies — what createApp uses when no `_deps` is injected. */
export const _realDeps: _CreateAppDeps = {
  getElementById: (id) => document.getElementById(id) as HTMLCanvasElement | null,
  createGraphicsDevice,
  newAppBase: (canvas) => new AppBase(canvas),
  fillMode: FILLMODE_FILL_WINDOW,
  resolutionMode: RESOLUTION_AUTO,
};

/**
 * Run the FROZEN guarantee-8 boot lifecycle (mirrors main.ts:26-116):
 *
 *   1. Canvas lookup (loud throw if missing).
 *   2. resolveDeviceTypes + createGraphicsDevice.
 *   3. detectTier + device.maxPixelRatio.
 *   4. AppOptions assembled by CONCATENATING module contributions.
 *   5. new AppBase + app.init(options).
 *   6. setCanvasFillMode(FILL_WINDOW) + setCanvasResolution(AUTO) + resizeCanvas().
 *   7. assertSystemsRegistered (hand-authored list from config).
 *   8. module init(app) hooks (no-op for an empty list).
 *   9. Scene build awaited via scenes.resolve(location.search).
 *  10. platform.init().
 *  11. app.start().
 *  12. warmup(app).
 *  13. setupContextLoss(app, canvas).
 *  14. platform.gameLoadingFinished().
 *  15. hooks.onReady?.({app, device, tier, platform}).
 *
 * Scene-build errors PROPAGATE (no catch — gate-14 Branch A).
 *
 * @param game The validated config (from defineGame).
 * @param _deps Optional injected dependencies for unit-test lifecycle-ordering assertions. Defaults
 *   to the real PlayCanvas functions. NEVER supply this in production code.
 */
export async function createApp(game: GameConfig, _deps?: Partial<_CreateAppDeps>): Promise<void> {
  const deps: _CreateAppDeps = { ..._realDeps, ..._deps };
  const onProgress = game.hooks?.onProgress;

  // ── 1. Canvas lookup ─────────────────────────────────────────────────────────
  const canvasId = game.canvas ?? 'app';
  const canvas = deps.getElementById(canvasId);
  if (!canvas) {
    throw new Error(`createApp: Missing <canvas id="${canvasId}"> in index.html`);
  }

  // ── 2. Device types + createGraphicsDevice ───────────────────────────────────
  const deviceTypes = resolveDeviceTypes({
    contentWebgpuEligible: game.tier.contentWebgpuEligible,
    force: game.tier.force,
  });
  // antialias is forwarded ONLY when the game pins it — an absent field keeps the call shape
  // (and the engine-default antialias:true) byte-identical for existing games (demo).
  const device = await deps.createGraphicsDevice(
    canvas,
    game.tier.antialias === undefined
      ? { deviceTypes }
      : { deviceTypes, antialias: game.tier.antialias },
  );
  onProgress?.('device-created');

  // ── 3. detectTier + DPR ─────────────────────────────────────────────────────
  const dprNav = {
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : undefined,
    hasFinePointer:
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(pointer: fine)').matches
        : undefined,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
  };
  const unmaskedRenderer = (device as { unmaskedRenderer?: string }).unmaskedRenderer ?? '';
  let tier = detectTier({ unmaskedRenderer }, dprNav);
  if (game.tier.postClassify) {
    tier = game.tier.postClassify(tier, { unmaskedRenderer, nav: dprNav });
  }
  device.maxPixelRatio = tier.maxPixelRatio;
  onProgress?.('tier-detected');

  // ── 4. AppOptions assembled from module contributions ────────────────────────
  const options = new AppOptions();
  options.graphicsDevice = device;
  options.componentSystems = game.modules.flatMap((m) => m.componentSystems ?? []) as unknown as (typeof ComponentSystem)[];
  options.resourceHandlers = game.modules.flatMap((m) => m.resourceHandlers ?? []);

  // ── 5. AppBase init ──────────────────────────────────────────────────────────
  const app = deps.newAppBase(canvas);
  app.init(options);
  onProgress?.('app-init');

  // ── 6. Fill mode / resolution / resize ──────────────────────────────────────
  app.setCanvasFillMode(deps.fillMode);
  app.setCanvasResolution(deps.resolutionMode);
  app.resizeCanvas();

  // ── 7. assertSystemsRegistered ───────────────────────────────────────────────
  assertSystemsRegistered(app, game.requiredSystems);
  onProgress?.('systems-asserted');

  // ── 8. Module init hooks (no-op for an empty list) ───────────────────────────
  for (const mod of game.modules) {
    if (mod.init) await mod.init(app);
  }
  onProgress?.('modules-init');

  // ── 9. Scene build (errors PROPAGATE — gate-14 Branch A) ────────────────────
  const search = typeof location !== 'undefined' ? location.search : '';
  const builder = game.scenes.resolve(search);
  await builder(app);
  onProgress?.('scene-built');

  // ── 10. platform.init() ──────────────────────────────────────────────────────
  await game.platform.init();
  onProgress?.('platform-init');

  // ── 11. app.start() ──────────────────────────────────────────────────────────
  app.start();
  onProgress?.('started');

  // ── 12. warmup ───────────────────────────────────────────────────────────────
  warmup(app);
  onProgress?.('warmed-up');

  // ── 13. setupContextLoss ─────────────────────────────────────────────────────
  setupContextLoss(app, canvas);
  onProgress?.('context-loss-wired');

  // ── 14. gameLoadingFinished ───────────────────────────────────────────────────
  game.platform.gameLoadingFinished();

  // ── 15. resize listener ───────────────────────────────────────────────────────
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      app.resizeCanvas();
    });
  }

  // ── 16. onReady ──────────────────────────────────────────────────────────────
  onProgress?.('ready');
  game.hooks?.onReady?.({ app, device, tier, platform: game.platform });
}
